'use client';

import { PageSpecific } from '.generated/Feature.CW.model';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { ComponentProps } from 'lib/component-props';
import {
  Field,
  ImageField,
  LinkField,
  RichTextField,
  TextField,
} from '@sitecore-content-sdk/nextjs';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import Link from 'next/link';
import { Data } from '.generated/Foundation.CW.model';
import { SvgIcon } from 'helpers/SvgIcon';
import { formatBlogDate } from 'lib/utils/utilityFunction';
import useIsEditing from 'lib/hooks/useIsEditing';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import { tv } from 'tailwind-variants';
import { CoveoFacetFields } from 'lib/constants';

export type FeaturedStoriesProps = ComponentProps &
  PageSpecific.FeaturedStories &
  Data.PageSpecific.Author;

interface FeaturedArticleItem {
  fields?: {
    heroImage?: ImageField;
    blogContentType?: TextField;
    blogPostTitle?: TextField;
    previewText?: RichTextField;
    author?: Data.PageSpecific.Author;
    date?: Field<string>;
    videoId?: Field<string>;
  };
  url?: string;
}

const FeaturedStories = (props: FeaturedStoriesProps) => {
  return <ExpandedVersion {...props} />;
};
export default FeaturedStories;

export const ExpandedVersion = (props: FeaturedStoriesProps) => {
  const isEditing = useIsEditing();
  const fullArticleCard = props?.fields?.featuredArticleList?.[0] as FeaturedArticleItem;
  const authorCardFields = fullArticleCard?.fields?.author;

  const secondaryArticleCard1 = props?.fields?.featuredArticleList?.[1] as FeaturedArticleItem;
  const secondaryArticleCard2 = props?.fields?.featuredArticleList?.[2] as FeaturedArticleItem;

  const siteSettingsField = useSiteSettings();
  const blogSearchPage = siteSettingsField?.fields?.blogSearchPage as LinkField;

  //   Date Format
  const fullCardDateString = fullArticleCard?.fields?.date;
  const secondaryCard1DateString = secondaryArticleCard1?.fields?.date;
  const secondaryCard2DateString = secondaryArticleCard2?.fields?.date;

  return (
    <section className="max-w-screen-xxl mx-auto py-[80px] w-full lg:px-[80px] px-10" role="group">
      <PlainTextWrapper
        tag="h2"
        field={props?.fields?.featuredStoriesTitle}
        className="pb-12 text-36 leading-43 font-bold font-centuryGothic"
      />
      <article
        role="region"
        aria-labelledby={`${fullArticleCard?.fields?.blogPostTitle?.value}`}
        className={`rounded-md overflow-hidden flex flex-col group shadow-default hover:shadow-hover`}
      >
        {isEditing ? (
          <div className="w-full aspect-[7/4] shrink-0 max-h-[550px]">
            <ImageWrapper
              field={fullArticleCard?.fields?.heroImage}
              className="w-full h-full object-cover object-left-top"
            />
          </div>
        ) : (
          <Link
            href={fullArticleCard?.url || ''}
            className="w-full aspect-[7/4] shrink-0 max-h-[550px]"
            prefetch={false}
          >
            <ImageWrapper
              field={fullArticleCard?.fields?.heroImage}
              className="w-full h-full object-cover object-left-top"
            />
          </Link>
        )}

        <div className="flex flex-col p-12">
          <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
            {isEditing ? (
              <PlainTextWrapper
                tag="p"
                field={fullArticleCard?.fields?.blogContentType}
                className=" text-blue-base font-bold "
              />
            ) : (
              <Link
                href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${fullArticleCard?.fields?.blogContentType?.value}`}
              >
                <PlainTextWrapper
                  tag="p"
                  field={fullArticleCard?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              </Link>
            )}

            <div className="text-[#111111]">{formatBlogDate(fullCardDateString?.value)}</div>
          </div>

          <div className="flex justify-between">
            {isEditing ? (
              <div className="flex-1">
                <PlainTextWrapper
                  tag="h3"
                  field={fullArticleCard?.fields?.blogPostTitle}
                  className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                />
              </div>
            ) : (
              <Link href={fullArticleCard?.url || ''} className="flex-1">
                <PlainTextWrapper
                  tag="h3"
                  field={fullArticleCard?.fields?.blogPostTitle}
                  className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                />
              </Link>
            )}

            <SvgIcon
              icon="arrow-right"
              size="lg"
              className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            />
          </div>

          <RichTextWrapper
            field={fullArticleCard?.fields?.previewText}
            className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
          />
          {(fullArticleCard?.fields?.author?.fields?.authorTitle?.value !== undefined ||
            fullArticleCard?.fields?.author?.fields?.authorName?.value !== undefined) && (
            <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
              {isEditing ? (
                <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                  <PlainTextWrapper field={fullArticleCard?.fields?.author?.fields?.authorName} />
                </div>
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogAuthorName}=${authorCardFields?.fields?.authorName?.value}`}
                  className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                >
                  <PlainTextWrapper field={fullArticleCard?.fields?.author?.fields?.authorName} />
                </Link>
              )}

              {fullArticleCard?.fields?.author?.fields?.authorTitle?.value?.trim() &&
                fullArticleCard?.fields?.author?.fields?.authorName?.value?.trim() &&
                '|'}

              <PlainTextWrapper
                tag="p"
                field={fullArticleCard?.fields?.author?.fields?.authorTitle}
                className=""
              />
            </div>
          )}
        </div>
      </article>

      {/* Secondary feature Cards */}
      <div className="flex flex-col lg:flex-row gap-[20px] mt-16 w-full">
        <article
          role="region"
          aria-labelledby={`${secondaryArticleCard1?.fields?.blogPostTitle?.value}`}
          className={`rounded-md overflow-hidden lg:w-1/2 flex flex-col group shadow-default hover:shadow-hover`}
        >
          {isEditing ? (
            <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
              <ImageWrapper
                field={secondaryArticleCard1?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </div>
          ) : (
            <Link
              href={secondaryArticleCard1?.url || ''}
              className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
            >
              <ImageWrapper
                field={secondaryArticleCard1?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </Link>
          )}

          <div className="flex flex-col p-12">
            <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
              {isEditing ? (
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard1?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${secondaryArticleCard1?.fields?.blogContentType?.value}`}
                >
                  <PlainTextWrapper
                    tag="p"
                    field={secondaryArticleCard1?.fields?.blogContentType}
                    className=" text-blue-base font-bold "
                  />
                </Link>
              )}

              <div className="text-[#111111]">
                {formatBlogDate(secondaryCard1DateString?.value)}
              </div>
            </div>

            <div className="flex justify-between">
              {isEditing ? (
                <div className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard1?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </div>
              ) : (
                <Link href={secondaryArticleCard1?.url || ''} className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard1?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </Link>
              )}

              <SvgIcon
                icon="arrow-right"
                size="lg"
                className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              />
            </div>

            <RichTextWrapper
              field={secondaryArticleCard1?.fields?.previewText}
              className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
            />
            {(secondaryArticleCard1?.fields?.author?.fields?.authorTitle?.value !== undefined ||
              secondaryArticleCard1?.fields?.author?.fields?.authorName?.value !== undefined) && (
              <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                {isEditing ? (
                  <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                    <PlainTextWrapper
                      field={secondaryArticleCard1?.fields?.author?.fields?.authorName}
                    />
                  </div>
                ) : (
                  <Link
                    href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogAuthorName}=${secondaryArticleCard1?.fields?.author?.fields?.authorName?.value}`}
                    className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                  >
                    <PlainTextWrapper
                      field={secondaryArticleCard1?.fields?.author?.fields?.authorName}
                    />
                  </Link>
                )}

                {secondaryArticleCard1?.fields?.author?.fields?.authorTitle?.value?.trim() &&
                  secondaryArticleCard1?.fields?.author?.fields?.authorName?.value?.trim() &&
                  '|'}

                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard1?.fields?.author?.fields?.authorTitle}
                  className=""
                />
              </div>
            )}
          </div>
        </article>
        <article
          role="region"
          aria-labelledby={`${secondaryArticleCard2?.fields?.blogPostTitle?.value}`}
          className={`rounded-md  overflow-hidden lg:w-1/2 flex flex-col group shadow-default hover:shadow-hover`}
        >
          {isEditing ? (
            <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
              <ImageWrapper
                field={secondaryArticleCard2?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </div>
          ) : (
            <Link
              href={secondaryArticleCard2?.url || ''}
              className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
            >
              <ImageWrapper
                field={secondaryArticleCard2?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </Link>
          )}

          <div className="flex flex-col p-12">
            <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
              {isEditing ? (
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard2?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${secondaryArticleCard2?.fields?.blogContentType?.value}`}
                >
                  <PlainTextWrapper
                    tag="p"
                    field={secondaryArticleCard2?.fields?.blogContentType}
                    className=" text-blue-base font-bold "
                  />
                </Link>
              )}

              <div className="text-[#111111]">
                {formatBlogDate(secondaryCard2DateString?.value)}
              </div>
            </div>

            <div className="flex justify-between">
              {isEditing ? (
                <div className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard2?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </div>
              ) : (
                <Link href={secondaryArticleCard2?.url || ''} className=" flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard2?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </Link>
              )}

              <SvgIcon
                icon="arrow-right"
                size="lg"
                className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              />
            </div>

            <RichTextWrapper
              field={secondaryArticleCard2?.fields?.previewText}
              className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
            />
            {(secondaryArticleCard2?.fields?.author?.fields?.authorTitle?.value !== undefined ||
              secondaryArticleCard2?.fields?.author?.fields?.authorName?.value !== undefined) && (
              <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                {isEditing ? (
                  <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                    <PlainTextWrapper
                      field={secondaryArticleCard2?.fields?.author?.fields?.authorName}
                    />
                  </div>
                ) : (
                  <Link
                    href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogAuthorName}=${authorCardFields?.fields?.authorName?.value}`}
                    className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                  >
                    <PlainTextWrapper
                      field={secondaryArticleCard2?.fields?.author?.fields?.authorName}
                    />
                  </Link>
                )}

                {secondaryArticleCard2?.fields?.author?.fields?.authorTitle?.value?.trim() &&
                  secondaryArticleCard2?.fields?.author?.fields?.authorName?.value?.trim() &&
                  '|'}
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard2?.fields?.author?.fields?.authorTitle}
                  className=""
                />
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
};

export const CondensedVersion = (props: FeaturedStoriesProps) => {
  const featuredArticles = props?.fields?.featuredArticleList as FeaturedArticleItem[];
  const isEditing = useIsEditing();
  const siteSettings = useSiteSettings()?.fields;
  const blogSearchPage = siteSettings?.blogSearchPage;

  return (
    <section className="max-w-screen-xxl mx-auto py-[80px] w-full lg:px-[80px] px-10" role="group">
      <PlainTextWrapper
        tag="h2"
        field={props?.fields?.featuredStoriesTitle}
        className="pb-12 text-36 leading-43 font-bold font-centuryGothic"
      />
      <ArticleCardGrid
        articles={featuredArticles}
        isEditing={isEditing}
        blogSearchPage={blogSearchPage}
      />
      {featuredArticles?.length !== 0 && (
        <LinkWrapper
          ctaVariant="link"
          ctaIcon="arrow-right"
          className="font-semibold text-18 leading-25 text-blue-base font-centuryGothic hover:text-darkblue-base focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline rounded-sm flex items-center mt-[32px] py-[14px] gap-[8px] w-fit"
          field={props?.fields?.viewMoreLink}
        />
      )}
    </section>
  );
};

const ArticleCardGrid = ({
  articles,
  isEditing,
  blogSearchPage,
}: {
  articles: FeaturedArticleItem[];
  isEditing: boolean;
  blogSearchPage?: LinkField;
}) => {
  return (
    <div className="lg:grid grid-cols-3 gap-[32px]">
      {articles?.map((listItem, index) => {
        return (
          <article
            role="region"
            aria-labelledby={`${listItem?.fields?.blogPostTitle?.value}`}
            key={index}
            className="rounded-md overflow-hidden flex flex-col group shadow-default hover:shadow-hover mb-[20px] lg:mb-0 last:mb-0 !bg-white"
          >
            {isEditing ? (
              <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
                <ImageWrapper
                  field={listItem?.fields?.heroImage}
                  className="w-full h-full object-cover object-left-top"
                />
              </div>
            ) : (
              <Link
                href={listItem?.url || ''}
                className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
              >
                <ImageWrapper
                  field={listItem?.fields?.heroImage}
                  className="w-full h-full object-cover object-left-top"
                />
              </Link>
            )}
            <div className="flex flex-col p-12">
              <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
                {isEditing ? (
                  <PlainTextWrapper
                    tag="p"
                    field={listItem?.fields?.blogContentType}
                    className=" text-blue-base font-bold hover:text-darkblue-base "
                  />
                ) : (
                  <Link
                    href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${listItem?.fields?.blogContentType?.value}`}
                  >
                    <PlainTextWrapper
                      tag="p"
                      field={listItem?.fields?.blogContentType}
                      className=" text-blue-base font-bold  hover:text-darkblue-base"
                    />
                  </Link>
                )}

                <div className="text-[#111111]">
                  {formatBlogDate(listItem?.fields?.date?.value)}
                </div>
              </div>

              <div className="flex justify-between">
                {isEditing ? (
                  <div className="flex-1">
                    <PlainTextWrapper
                      tag="h3"
                      field={listItem?.fields?.blogPostTitle}
                      className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                    />
                  </div>
                ) : (
                  <Link href={listItem?.url || ''} className="flex-1">
                    <PlainTextWrapper
                      tag="h3"
                      field={listItem?.fields?.blogPostTitle}
                      className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                    />
                  </Link>
                )}

                <SvgIcon
                  icon="arrow-right"
                  size="lg"
                  className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                />
              </div>

              <RichTextWrapper
                field={listItem?.fields?.previewText}
                className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
              />
              {(listItem?.fields?.author?.fields?.authorTitle?.value !== undefined ||
                listItem?.fields?.author?.fields?.authorName?.value !== undefined) && (
                <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                  {isEditing ? (
                    <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                      <PlainTextWrapper field={listItem?.fields?.author?.fields?.authorName} />
                    </div>
                  ) : (
                    <Link
                      href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogAuthorName}=${listItem?.fields?.author?.fields?.authorName?.value}`}
                      className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                    >
                      <PlainTextWrapper field={listItem?.fields?.author?.fields?.authorName} />
                    </Link>
                  )}

                  {listItem?.fields?.author?.fields?.authorTitle?.value?.trim() &&
                    listItem?.fields?.author?.fields?.authorName?.value?.trim() &&
                    '|'}

                  <PlainTextWrapper
                    tag="p"
                    field={listItem?.fields?.author?.fields?.authorTitle}
                    className=""
                  />
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export const VideoVersion = (props: FeaturedStoriesProps) => {
  const featuredArticles = props?.fields?.featuredArticleList as FeaturedArticleItem[];
  const { imageClass, videoClass } = TailwindVariants();
  const isEditing = useIsEditing();
  const siteSettings = useSiteSettings()?.fields;
  const blogSearchPage = siteSettings?.blogSearchPage;

  const nextThreeArticles = featuredArticles?.slice(1, 4) || [];
  const videoArticle = featuredArticles?.[0];

  return (
    <section className="hidden lg:block bg-blue-base py-[80px] w-full">
      <div className="max-w-screen-xxl mx-auto w-full lg:px-[80px] px-10">
        <PlainTextWrapper
          tag="h2"
          field={props?.fields?.featuredStoriesTitle}
          className="mb-10 pb-10 text-36 leading-43 font-bold font-centuryGothic text-white border-b-4 border-white"
        />
        <div className="flex gap-[32px] mb-[64px]">
          {/* Video Section */}
          <div className="w-1/2">
            {videoArticle?.fields?.videoId?.value !== '' ? (
              <div
                role="region"
                aria-labelledby="Wistia video player container"
                className={videoClass()}
              >
                <WistiaPlayer
                  mediaId={videoArticle?.fields?.videoId?.value || ''}
                  controlsVisibleOnLoad={false}
                  endVideoBehavior="reset"
                />
              </div>
            ) : (
              <ImageWrapper field={videoArticle?.fields?.heroImage} className={imageClass()} />
            )}
          </div>

          {/* Content Section */}
          <div className="w-1/2 flex flex-col justify-between text-white">
            <div>
              {isEditing ? (
                <PlainTextWrapper
                  tag="h3"
                  field={videoArticle?.fields?.blogPostTitle}
                  className="text-white text-22 leading-31 font-bold font-centuryGothic pb-4 mb-4"
                />
              ) : (
                <Link href={videoArticle?.url || ''}>
                  <PlainTextWrapper
                    tag="h3"
                    field={videoArticle?.fields?.blogPostTitle}
                    className="text-white text-22 leading-31 font-bold font-centuryGothic pb-4 mb-4 hover:underline"
                  />
                </Link>
              )}

              <RichTextWrapper
                field={videoArticle?.fields?.previewText}
                className="text-16 leading-25 font-[500] font-nunito text-white"
              />
            </div>
          </div>
        </div>
        {/* Next 3 Articles */}
        {nextThreeArticles && nextThreeArticles.length > 0 && (
          <ArticleCardGrid
            articles={nextThreeArticles}
            isEditing={isEditing}
            blogSearchPage={blogSearchPage}
          />
        )}
        {featuredArticles?.length !== 0 && (
          <LinkWrapper
            ctaVariant="link"
            ctaIcon="arrow-right"
            className="font-semibold text-18 leading-25 text-white font-centuryGothic hover:underline focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline rounded-sm flex items-center mt-[32px] py-[14px] gap-[8px] w-fit"
            field={props?.fields?.viewMoreLink}
          />
        )}
      </div>
    </section>
  );
};
export const TailwindVariants = tv({
  slots: {
    wrapper:
      'mx-auto max-w-screen-xxl lg:px-20 px-10 flex md:flex-row w-full order-last md:order-first gap-16 md:gap-[48px]',
    titleClass: 'font-centuryGothic text-theme-text font-bold mb-8',
    descriptionText: 'font-nunitoSans text-16 leading-25 font-[500] text-theme-text',
    imageClass:
      'w-full h-full object-cover object-center aspect-[3/2] md:rounded-lg rounded-md object-cover object-center aspect-[3/2]',
    videoClass: 'md:rounded-lg rounded-md overflow-hidden aspect-video',
    buttonWrapperClass: 'md:mb-[24px] mb-6 md:!gap-12 !gap-6 md:flex-row flex-col',
  },
});
